import mailIcon from "@assets/icons/mail-fill.svg";
import phoneIcon from "@assets/icons/phone-fill.svg";
import buildingIcon from "@assets/icons/building-2-fill.svg";

interface ContactDetail {
  title: string;
  icon: string;
}

interface ContactProps {
  title?: string;
  subtitle?: string;
  contactDetails?: ContactDetail[];
  cardTitle?: string;
}

const defaultContactDetails: ContactDetail[] = [
  {
    title: "help@gasis.ae",
    icon: mailIcon,
  },
  {
    title: "Support (8AM – 8PM)",
    icon: phoneIcon,
  },
  {
    title: "Sunday – Thursday (8AM – 6PM)",
    icon: buildingIcon,
  },
];

export default function Contact({
  title = "Need Help with your Bill or Account",
  subtitle = "Call our 24/7 Emergency Line: 800-GASIS (42747) or report a concern via our Online Safety Form.",
  contactDetails = defaultContactDetails,
  cardTitle,
}: ContactProps) {
  return (
    <section className="py-16 lg:py-[102px] px-5 md:px-10 bg-[#FFFCFB]">
      <div className="w-full max-w-[1368px] mx-auto">
        {/* CTA Card */}
        <div className="w-full h-auto lg:h-[440px] rounded-[16px] bg-[#f03813] overflow-hidden relative flex items-center">
          {/* Decorative Lines - Hidden on Mobile */}
          <div className="absolute inset-0 hidden scale-100 pointer-events-none lg:block">
            {/* Top vertical bars */}
            <div
              className="absolute w-[31px] h-[251px] bg-[#fff8f6] hidden xl:block"
              style={{ top: "-112px", right: "550px" }}
            />
            <div
              className="absolute w-[31px] h-[251px] bg-[#fff8f6]"
              style={{ top: "-150px", right: "450px" }}
            />
            <div
              className="absolute w-[31px] h-[251px] bg-[#fff8f6]"
              style={{ top: "-180px", right: "260px" }}
            />
            <div
              className="absolute w-[31px] h-[251px] bg-[#fff8f6]"
              style={{
                top: "-173.03px",
                right: "360px",
                transform: "rotate(-3.3deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[251px] bg-[#fff8f6]"
              style={{
                top: "-154.01px",
                right: "100px",
                transform: "rotate(14.8deg)",
                transformOrigin: "0 0",
              }}
            />
            {/* Curved right side bars */}
            <div
              className="absolute w-[31px] h-[200px] bg-[#fff8f6]"
              style={{
                top: "10px",
                right: "-60px",
                transform: "rotate(52.5deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[100px] bg-[#fff8f6]"
              style={{
                top: "180px",
                right: "-40px",
                transform: "rotate(83.3deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[140px] bg-[#fff8f6]"
              style={{
                top: "320px",
                right: "-60px",
                transform: "rotate(114.1deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[130px] bg-[#fff8f6]"
              style={{
                top: "450px",
                right: "-60px",
                transform: "rotate(144.9deg)",
                transformOrigin: "0 0",
              }}
            />
            {/* Bottom bars - pointing left/up (scaled for 1368px container) */}
            <div
              className="absolute w-[31px] h-[302.5px] bg-[#fff8f6]"
              style={{
                bottom: "-420px",
                right: "-30px",
                transform: "rotate(144.9deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[302.5px] bg-[#fff8f6]"
              style={{
                bottom: "-510px",
                right: "192px",
                transform: "rotate(175.8deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[302.5px] bg-[#fff8f6]"
              style={{
                bottom: "-525px",
                right: "324px",
                transform: "rotate(-176.1deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[302.5px] bg-[#fff8f6]"
              style={{
                bottom: "-525px",
                right: "409px",
                transform: "rotate(-176.1deg)",
                transformOrigin: "0 0",
              }}
            />
            <div
              className="absolute w-[31px] h-[302.5px] bg-[#fff8f6] hidden xl:block"
              style={{
                bottom: "-490px",
                right: "516px",
                transform: "rotate(-176.1deg)",
                transformOrigin: "0 0",
              }}
            />
          </div>

          {/* Content */}
          <div className="relative z-10 w-full flex flex-col lg:flex-row items-center justify-between gap-10 py-6 lg:py-[91.7px] px-4 lg:px-[55px]">
            <div className="pt-5">
              <h2 className="text-[28px] md:text-[40px] lg:text-[54px] xl:text-[60px] font-bold text-center lg:text-left text-[#fffcfb] lg:max-w-[643px]">
                {title}
              </h2>
              <p className="text-[14px] lg:text-[20px] xl:text-[28px] md:font-medium text-center lg:text-left leading-snug text-[#fffcfb] max-w-[640px] mt-5 lg:mt-[47px]">
                {subtitle}
              </p>
            </div>

            <div className="lg:pr-14 xl:pr-28">
              <div className="bg-white p-3 sm:p-4 md:p-6 lg:px-[24px] lg:py-[20px] space-y-2.5 rounded-[12px] md:rounded-[24px] md:w-[600px] w-full lg:max-w-[400px] mx-auto lg:mx-0">
                {cardTitle && (
                  <h3 className="text-[#20262B] text-[18px] sm:text-[20px] md:text-[24px] lg:text-[28px] font-bold mb-[10px]">
                    {cardTitle}
                  </h3>
                )}
                {contactDetails.map((data, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <img
                      src={data.icon}
                      alt="Icon"
                      className="size-4 sm:size-5"
                    />
                    <span className="text-[13px] sm:text-[14px] md:text-[16px] font-medium text-[#20262B]">
                      {data.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
